import { recordReloadRecovery, reportImportRecoveryOutcome, reportRetryRecovery } from 'lib/telemetry/autoRecovery';
import { CHUNK_RELOAD_AT_KEY, CHUNK_RELOAD_COOLDOWN_MS } from 'lib/telemetry/chunkReloadScript';

import { reloadPage } from './reloadPage';
import { getSessionStorageItem, setSessionStorageItem } from './storage';

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 500;

function isChunkLoadError(error: unknown): error is Error {
    return error instanceof Error && error.name === 'ChunkLoadError';
}

/**
 * Without a guard, a chunk that fails on every load (CDN outage, blocked by a
 * proxy) reloads the page forever. Shares the inline chunk-reload script's
 * cooldown so the two can't loop each other. Like `persistVersionSkewReloadGuard`,
 * the stamp must read back before it counts: unpersisted, reloading could loop.
 */
function claimChunkReload(): boolean {
    if (Date.now() - Number(getSessionStorageItem(CHUNK_RELOAD_AT_KEY) || 0) < CHUNK_RELOAD_COOLDOWN_MS) {
        return false;
    }
    const stamp = String(Date.now());
    setSessionStorageItem(CHUNK_RELOAD_AT_KEY, stamp);
    return getSessionStorageItem(CHUNK_RELOAD_AT_KEY) === stamp;
}

export async function importWithRetry<T>(
    importFn: () => Promise<T>,
    retries = MAX_RETRIES,
    delay = INITIAL_RETRY_DELAY_MS,
): Promise<T> {
    let recoveryId: string | undefined;
    let startedAt = 0;
    for (;;) {
        try {
            const loadedModule = await importFn();
            if (recoveryId) reportImportRecoveryOutcome(recoveryId, startedAt, 'import_succeeded');
            return loadedModule;
        } catch (error) {
            if (isChunkLoadError(error) && retries > 0) {
                if (!startedAt) startedAt = Date.now();
                recoveryId =
                    reportRetryRecovery(
                        'chunk_load',
                        'import_with_retry',
                        { retries_remaining: retries, delay_ms: delay },
                        error,
                        recoveryId,
                    ) ?? recoveryId;
                console.warn(`ChunkLoadError, retrying in ${delay}ms (${retries} retries left)`);
                await new Promise((resolve) => setTimeout(resolve, delay));
                retries--;
                delay *= 2;
                continue;
            }

            if (recoveryId) reportImportRecoveryOutcome(recoveryId, startedAt, 'import_failed');
            if (isChunkLoadError(error)) {
                if (claimChunkReload()) {
                    console.warn('ChunkLoadError, all retries exhausted - reloading page');
                    recordReloadRecovery('chunk_load', 'import_with_retry', error, recoveryId);
                    reloadPage();
                } else {
                    // Not reported as blocked: for lazy components the rethrow reaches
                    // ChunkSkewErrorBoundary, which may still reload, and reports if it can't.
                    console.warn('ChunkLoadError, all retries exhausted - reload skipped by the reload-loop guard');
                }
            }
            throw error;
        }
    }
}
